import {
  adminAwardSchema,
  AWARD_RESULT_LABELS,
  AWARD_RESULTS,
  awardResultSchema,
  mediaVariant,
  type AdminAward,
  type AwardResult,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateInput,
  Field,
  Input,
  moduleOrigins,
  Notice,
  Select,
  StatusBadge,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { IMAGE_ASPECTS, ImageField, useImagesBusy } from '../media/ImageUpload';

const BASE = '/api/site/content/premios';

/** Mesmo ano e resultado: so ai a ordem a mao conta. */
const sameGroup = (a: AdminAward | undefined, b: AdminAward | undefined) =>
  a !== undefined && b !== undefined && a.year === b.year && a.result === b.result;

/**
 * Premios recebidos (decidido pelo GrowuP a 20-10-2026): os que outras
 * entidades deram a Grow uP, em growup.gg/premios. Comeca vazio. Ver
 * docs/conteudos.
 */
export function AwardsView() {
  const [list, setList] = useState<AdminAward[] | null>(null);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  // Muda so ao abrir o editor: criar um premio nao o volta a montar.
  const [opened, setOpened] = useState(0);
  const open = (id: string | 'new') => {
    setOpened((n) => n + 1);
    setEditing(id);
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getJson(BASE, z.array(adminAwardSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const swap = async (i: number, j: number) => {
    if (!list) return;
    const ids = list.map((a) => a.id);
    const a = ids[i];
    const b = ids[j];
    if (a === undefined || b === undefined) return;
    ids[i] = b;
    ids[j] = a;
    setBusy(true);
    setError(null);
    try {
      setList(z.array(adminAwardSchema).parse(await send('PUT', `${BASE}/order`, { ids })));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (editing !== null) {
    const current = list?.find((a) => a.id === editing) ?? null;
    return (
      <AwardEditor
        key={opened}
        award={current}
        onSaved={(saved) => {
          setList((l) => {
            if (!l) return [saved];
            return l.some((a) => a.id === saved.id)
              ? l.map((a) => (a.id === saved.id ? saved : a))
              : [...l, saved];
          });
          setEditing(saved.id);
        }}
        onClose={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  const published = list?.filter((a) => a.status === 'published').length ?? 0;
  let lastYear: number | null = null;

  return (
    <Card
      headingLevel={2}
      title="Prémios recebidos"
      actions={
        <Button size="sm" onClick={() => open('new')}>
          Novo prémio
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Os prémios que a Grow uP recebeu de outras entidades, em growup.gg/premios. O site
          mostra-os por ano, do mais recente para o mais antigo; no mesmo ano, vencedor, finalista,
          nomeado e menção. As setas mudam a ordem dentro do mesmo ano e resultado. Os marcados como
          destaque aparecem também em Sobre nós.{' '}
          {list ? `${String(published)} no site agora.` : null}
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {list === null && !error ? <p>A carregar…</p> : null}
        {list && list.length === 0 ? <p>Ainda não há prémios.</p> : null}
        <ol className="flex flex-col gap-3">
          {list?.map((a, i) => {
            const heading = a.year !== lastYear ? a.year : null;
            lastYear = a.year;
            return (
              <li key={a.id} className="flex flex-col gap-2">
                {heading !== null ? (
                  <h3 className="mt-2 font-display text-title text-gu-ink">{heading}</h3>
                ) : null}
                <div className="flex flex-wrap items-center gap-4 border-2 border-gu-line p-2">
                  {a.imageUrl ? (
                    <img
                      src={mediaVariant(a.imageUrl, 'sm')}
                      alt={a.imageAlt}
                      className="aspect-square w-14 bg-gu-sunken object-cover"
                    />
                  ) : (
                    <TrophyTile result={a.result} />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="font-semibold">
                      {a.title}
                      {a.featured ? (
                        <span className="text-gu-accent" title="Destaque em Sobre nós">
                          {' '}
                          ★
                        </span>
                      ) : null}
                    </span>
                    <span className="text-caption text-gu-ink-muted">
                      {a.awardedBy} · {AWARD_RESULT_LABELS[a.result]}
                      {a.category ? ` · ${a.category}` : ''}
                    </span>
                    <span>
                      <StatusBadge tone={a.status === 'published' ? 'success' : 'neutral'}>
                        {a.status === 'published' ? 'No site' : 'Rascunho'}
                      </StatusBadge>
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy || !sameGroup(list[i - 1], a)}
                      aria-label={`Subir ${a.title}`}
                      onClick={() => void swap(i, i - 1)}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy || !sameGroup(a, list[i + 1])}
                      aria-label={`Descer ${a.title}`}
                      onClick={() => void swap(i, i + 1)}
                    >
                      ↓
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => open(a.id)}>
                      Editar
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </Card>
  );
}

/** O trofeu das medalhas do Hall of Fame (como em `site-web/src/content/Awards.tsx`). */
const TROPHY_PATH =
  'M7 3h10v2h3v3c0 2.8-2 5-4.6 5.4-.8 1.6-2 2.6-3.4 2.8V19h3v2H9v-2h3v-2.8c-1.4-.2-2.6-1.2-3.4-2.8C6 13 4 10.8 4 8V5h3V3zM6 7v1c0 1.5.8 2.8 2 3.4-.3-.9-.5-1.9-.5-2.9V7H6zm12 0h-1.5v1.5c0 1-.2 2-.5 2.9 1.2-.6 2-1.9 2-3.4V7z';

/** Sem imagem: um trofeu na cor do resultado, como no site. */
function TrophyTile({ result }: { result: AwardResult }) {
  const tone =
    result === 'winner'
      ? 'text-gu-accent'
      : result === 'finalist'
        ? 'text-gu-ink'
        : 'text-gu-ink-muted';
  return (
    <div
      aria-hidden="true"
      className="flex aspect-square w-14 items-center justify-center bg-gu-sunken"
    >
      <svg viewBox="0 0 24 24" className={`size-7 ${tone}`}>
        <path d={TROPHY_PATH} fill="currentColor" />
      </svg>
    </div>
  );
}

const thisYear = new Date().getFullYear();

function AwardEditor({
  award,
  onSaved,
  onClose,
}: {
  award: AdminAward | null;
  onSaved: (a: AdminAward) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(award?.title ?? '');
  const [awardedBy, setAwardedBy] = useState(award?.awardedBy ?? '');
  const [year, setYear] = useState(award?.year.toString() ?? String(thisYear));
  const [awardedOn, setAwardedOn] = useState(award?.awardedOn ?? '');
  const [result, setResult] = useState<AwardResult>(award?.result ?? 'winner');
  const [category, setCategory] = useState(award?.category ?? '');
  const [description, setDescription] = useState(award?.description ?? '');
  const [imageUrl, setImageUrl] = useState<string | null>(award?.imageUrl ?? null);
  const [imageAlt, setImageAlt] = useState(award?.imageAlt ?? '');
  const [linkUrl, setLinkUrl] = useState(award?.linkUrl ?? '');
  const [featured, setFeatured] = useState(award?.featured ?? false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const images = useImagesBusy();

  const isPublished = award?.status === 'published';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      title,
      awardedBy,
      year: Number(year),
      awardedOn: awardedOn === '' ? null : awardedOn,
      result,
      category,
      description,
      imageUrl,
      imageAlt: imageUrl ? imageAlt : '',
      linkUrl,
      featured,
    };
    setBusy(true);
    setError(null);
    setSaved(null);
    setFields({});
    try {
      const res = award
        ? await send('PUT', `${BASE}/${award.id}`, payload)
        : await send('POST', BASE, payload);
      onSaved(adminAwardSchema.parse(res));
      setSaved(award ? 'Guardado.' : 'Criado como rascunho.');
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const run = async (path: string) => {
    if (!award) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      onSaved(adminAwardSchema.parse(await send('POST', `${BASE}/${award.id}${path}`)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!award) return;
    if (!window.confirm(`Apagar o prémio «${award.title}»? Não se pode desfazer.`)) return;
    setBusy(true);
    try {
      await send('DELETE', `${BASE}/${award.id}`);
      onClose();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Prémios recebidos
        </Button>
      </div>
      <Card headingLevel={2} title={award ? award.title : 'Novo prémio'}>
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          <div className="grid items-start gap-4 md:grid-cols-[14rem_1fr]">
            <div className="flex flex-col gap-3">
              <ImageField
                label="Imagem"
                optional
                value={imageUrl}
                onChange={setImageUrl}
                aspect={IMAGE_ASPECTS.award}
                onBusyChange={images.track('imagem')}
                error={fields.imageUrl}
                hint="Quadrada: o troféu, o logótipo de quem deu o prémio ou uma foto da entrega. Sem imagem, o site mostra um troféu."
              />
              {imageUrl ? (
                <Field
                  label="Descrição da imagem"
                  hint="Para quem não a vê."
                  error={fields.imageAlt}
                >
                  <Input
                    value={imageAlt}
                    maxLength={300}
                    onChange={(e) => setImageAlt(e.target.value)}
                  />
                </Field>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Nome do prémio"
                hint="Como quem o deu o escreve."
                error={fields.title}
                className="sm:col-span-2"
              >
                <Input value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} />
              </Field>
              <Field
                label="Quem atribuiu"
                hint="Gala, câmara, federação, revista…"
                error={fields.awardedBy}
                className="sm:col-span-2"
              >
                <Input
                  value={awardedBy}
                  maxLength={140}
                  onChange={(e) => setAwardedBy(e.target.value)}
                />
              </Field>
              <Field label="Resultado" error={fields.result}>
                <Select
                  value={result}
                  onChange={(e) => setResult(awardResultSchema.parse(e.target.value))}
                >
                  {AWARD_RESULTS.map((r) => (
                    <option key={r} value={r}>
                      {AWARD_RESULT_LABELS[r]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Categoria"
                optional
                hint="Ex.: «Melhor Organização»."
                error={fields.category}
              >
                <Input
                  value={category}
                  maxLength={120}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </Field>
              <Field label="Ano" error={fields.year}>
                <Input
                  inputMode="numeric"
                  placeholder="aaaa"
                  maxLength={4}
                  value={year}
                  onChange={(e) => setYear(e.target.value.replace(/\D/g, ''))}
                />
              </Field>
              <Field label="Data da entrega" optional error={fields.awardedOn}>
                <DateInput value={awardedOn} onChange={setAwardedOn} />
              </Field>
            </div>
          </div>
          <Field
            label="Descrição curta"
            optional
            hint="Uma ou duas frases: porquê, ou quem da Grow uP o recebeu."
            error={fields.description}
          >
            <Textarea
              rows={3}
              value={description}
              maxLength={300}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field
            label="Ligação"
            optional
            hint="Uma notícia do site (/noticias/…) ou a página de quem deu o prémio (https://…)."
            error={fields.linkUrl}
          >
            <Input value={linkUrl} maxLength={500} onChange={(e) => setLinkUrl(e.target.value)} />
          </Field>
          <Checkbox
            label="Destaque em Sobre nós"
            checked={featured}
            onChange={(e) => setFeatured(e.target.checked)}
          />
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={busy || images.busy}>
              {award ? 'Guardar' : 'Criar rascunho'}
            </Button>
            {award && !isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void run('/publish')}
              >
                Publicar
              </Button>
            ) : null}
            {isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void run('/unpublish')}
              >
                Tirar do site
              </Button>
            ) : null}
            {award && !isPublished ? (
              <Button type="button" variant="ghost" disabled={busy} onClick={() => void remove()}>
                Apagar
              </Button>
            ) : null}
          </div>
          {award ? (
            <p className="text-caption text-gu-ink-muted">
              {isPublished ? (
                <>
                  No site.{' '}
                  <a
                    className="gu-link"
                    href={`${moduleOrigins().site}/premios`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver no site
                  </a>
                  .{' '}
                </>
              ) : (
                'Rascunho: ainda não aparece no site. '
              )}
              Última alteração: {formatDateTime(award.updatedAt)} por {award.updatedBy}.
            </p>
          ) : null}
        </form>
      </Card>
    </div>
  );
}
